<template>
    <div class="classes-page">
        <PageSpinner :onloader="onloader" />
        <base-header type="gradient-info" class="pb-6 pb-8 pt-5 pt-md-8">
            <router-link to="/dashboard" class="text-white">
                Dashboard
            </router-link>
            <router-link to="/school/academic-management" class="text-white">
                <i class="fa fa-ellipsis-v px-2"></i>Academic Management
            </router-link>
            <span class="text-white"><i class="fa fa-ellipsis-v px-2"></i>Classes</span>
        </base-header>

        <div class="container-fluid mt--7 classes-page__body">
            <section class="classes-panel" aria-labelledby="classes-page-title">
                <header class="classes-panel__intro">
                   

                    <div class="classes-panel__side">
                        <div v-if="totalRecords !== null" class="classes-stat">
                            <strong>{{ totalRecords }}</strong>
                            <span>{{ totalRecords === 1 ? "class" : "classes" }}</span>
                        </div>
                        <button type="button" class="btn btn-success" @click="openCreate">
                            <i class="fa fa-plus" aria-hidden="true"></i>
                            <span>New class</span>
                        </button>
                    </div>
                </header>

                <header class="classes-toolbar">
                    <div class="classes-toolbar__title">
                        <h2>Class register</h2>
                        <p class="classes-toolbar__meta">{{ resultsLabel }}</p>
                    </div>

                    <div class="classes-toolbar__tools">
                        <label class="classes-page-size">
                            <span>Show</span>
                            <select v-model.number="pageSize" aria-label="Classes per page">
                                <option :value="5">5</option>
                                <option :value="10">10</option>
                                <option :value="20">20</option>
                                <option :value="50">50</option>
                                <option :value="100">100</option>
                            </select>
                        </label>

                        <div class="classes-search">
                            <i class="fa fa-search" aria-hidden="true"></i>
                            <input
                                v-model="searchval"
                                type="search"
                                class="classes-search__input"
                                placeholder="Search class or category"
                                aria-label="Search classes by class or category name"
                                @keyup.enter="runSearchNow"
                            />
                            <button
                                v-if="searchval"
                                type="button"
                                class="classes-search__clear"
                                aria-label="Clear search"
                                @click="clearSearch"
                            >
                                <i class="fa fa-times" aria-hidden="true"></i>
                            </button>
                        </div>
                    </div>
                </header>

                <div v-if="initialLoading" class="classes-state">
                    <i class="fa fa-spinner fa-spin" aria-hidden="true"></i>
                    <strong>Loading classes</strong>
                    <span>Fetching the class register.</span>
                </div>

                <div v-else-if="loadFailed && !classes.length" class="classes-state">
                    <i class="fa fa-exclamation-circle" aria-hidden="true"></i>
                    <strong>Classes could not be loaded</strong>
                    <span>Check your connection and try again.</span>
                    <button type="button" class="btn classes-ghost-button" @click="retryLoad">
                        <i class="fa fa-refresh" aria-hidden="true"></i>
                        <span>Try again</span>
                    </button>
                </div>

                <div v-else-if="isFetching && !classes.length" class="classes-state">
                    <i class="fa fa-spinner fa-spin" aria-hidden="true"></i>
                    <strong>Searching classes</strong>
                    <span>Matching class records will appear shortly.</span>
                </div>

                <div v-else-if="!classes.length" class="classes-state">
                    <i class="fa fa-graduation-cap" aria-hidden="true"></i>
                    <template v-if="hasSearch">
                        <strong>No classes match “{{ searchval.trim() }}”</strong>
                        <span>Try a different class or category name.</span>
                        <button type="button" class="btn classes-ghost-button" @click="clearSearch">
                            Clear search
                        </button>
                    </template>
                    <template v-else>
                        <strong>No classes yet</strong>
                        <span>Create the first class to start organising the academic structure.</span>
                        <button type="button" class="btn classes-ghost-button" @click="openCreate">
                            <i class="fa fa-plus" aria-hidden="true"></i>
                            <span>Add a class</span>
                        </button>
                    </template>
                </div>

                <template v-else>
                    <div class="classes-table-wrap" :class="{ 'is-loading': isFetching }">
                        <table class="classes-table">
                            <caption class="sr-only">Classes and their assessment weighting</caption>
                            <thead>
                                <tr>
                                    <th scope="col">Class</th>
                                    <th scope="col">Category</th>
                                    <th scope="col" class="is-center" title="Exam score">Exam</th>
                                    <th scope="col" class="is-center" title="Continuous assessment score">CA</th>
                                    <th scope="col" class="is-center" title="Mid-term score">Mid term</th>
                                    <th scope="col" class="is-center" title="Topic score">Topic</th>
                                    <th scope="col" class="is-center" title="Combined assessment weighting">Total</th>
                                    <th scope="col" class="classes-table__action-head">
                                        <span class="sr-only">Actions</span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(item, index) in classes" :key="rowKey(item, index)">
                                    <td>
                                        <span class="classes-table__name">{{ item.className || "—" }}</span>
                                    </td>
                                    <td>
                                        <span class="classes-category">{{ item.categoryName || "—" }}</span>
                                    </td>
                                    <td class="is-center classes-table__score">{{ formatScore(item.examScore) }}</td>
                                    <td class="is-center classes-table__score">{{ formatScore(item.caScore) }}</td>
                                    <td class="is-center classes-table__score">{{ formatScore(item.midTermScore) }}</td>
                                    <td class="is-center classes-table__score">{{ formatScore(item.topicScore) }}</td>
                                    <td class="is-center">
                                        <span
                                            class="classes-weight"
                                            :class="weightClass(item)"
                                        >{{ formatWeight(item) }}</span>
                                    </td>
                                    <td class="classes-table__action">
                                        <button
                                            type="button"
                                            class="classes-edit"
                                            :aria-label="editLabel(item)"
                                            @click="editClass(item)"
                                        >
                                            <i class="fa fa-pencil" aria-hidden="true"></i>
                                            <span>Edit</span>
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div class="classes-cards" :class="{ 'is-loading': isFetching }">
                        <article
                            v-for="(item, index) in classes"
                            :key="rowKey(item, index)"
                            class="class-card"
                        >
                            <div class="class-card__head">
                                <div class="class-card__identity">
                                    <span class="class-card__name">{{ item.className || "—" }}</span>
                                    <span class="class-card__category">{{ item.categoryName || "—" }}</span>
                                </div>
                                <span class="classes-weight" :class="weightClass(item)">
                                    {{ formatWeight(item) }}
                                </span>
                            </div>

                            <dl class="class-card__scores">
                                <div>
                                    <dt>Exam</dt>
                                    <dd>{{ formatScore(item.examScore) }}</dd>
                                </div>
                                <div>
                                    <dt>CA</dt>
                                    <dd>{{ formatScore(item.caScore) }}</dd>
                                </div>
                                <div>
                                    <dt>Mid term</dt>
                                    <dd>{{ formatScore(item.midTermScore) }}</dd>
                                </div>
                                <div>
                                    <dt>Topic</dt>
                                    <dd>{{ formatScore(item.topicScore) }}</dd>
                                </div>
                            </dl>

                            <div class="class-card__actions">
                                <button
                                    type="button"
                                    class="btn classes-ghost-button"
                                    :aria-label="editLabel(item)"
                                    @click="editClass(item)"
                                >
                                    <i class="fa fa-pencil" aria-hidden="true"></i>
                                    <span>Edit class</span>
                                </button>
                            </div>
                        </article>
                    </div>
                </template>

                <footer v-if="totalPages > 1" class="classes-pagination">
                    <p class="classes-pagination__info">{{ paginationInfo }}</p>
                    <nav class="classes-pagination__controls" aria-label="Class pages">
                        <button
                            type="button"
                            class="classes-page-btn"
                            :disabled="currentPage <= 1 || isFetching"
                            aria-label="Previous page"
                            @click="prevPage"
                        >
                            <i class="fa fa-chevron-left" aria-hidden="true"></i>
                        </button>

                        <template v-for="(page, index) in paginationNumbers">
                            <span
                                v-if="page === '…'"
                                :key="'gap-' + index"
                                class="classes-page-gap"
                                aria-hidden="true"
                            >…</span>
                            <button
                                v-else
                                :key="'page-' + page"
                                type="button"
                                class="classes-page-btn"
                                :class="{ 'is-active': page === currentPage }"
                                :disabled="isFetching"
                                :aria-current="page === currentPage ? 'page' : null"
                                @click="goToPage(page)"
                            >
                                {{ page }}
                            </button>
                        </template>

                        <button
                            type="button"
                            class="classes-page-btn"
                            :disabled="currentPage >= totalPages || isFetching"
                            aria-label="Next page"
                            @click="nextPage"
                        >
                            <i class="fa fa-chevron-right" aria-hidden="true"></i>
                        </button>
                    </nav>
                </footer>
            </section>
        </div>

        <modal
            :show.sync="formVisible"
            modal-classes="modal-dialog-centered"
            @close="handleFormClosed"
        >
            <div class="classes-drawer">
                <header class="classes-drawer__head">
                    <span class="classes-drawer__icon" aria-hidden="true">
                        <i class="fa" :class="editingId ? 'fa-pencil' : 'fa-graduation-cap'"></i>
                    </span>
                    <div class="classes-drawer__heading">
                        <span class="classes-drawer__eyebrow">
                            {{ editingId ? "UPDATE RECORD" : "NEW RECORD" }}
                        </span>
                        <h2>{{ editingId ? "Edit class" : "Add a class" }}</h2>
                        <p v-if="editingLabel">{{ editingLabel }}</p>
                        <p v-else>Set the level, category and assessment weighting for this class.</p>
                    </div>
                    <button
                        type="button"
                        class="classes-drawer__close"
                        aria-label="Close"
                        @click="closeForm"
                    >
                        <i class="fa fa-times" aria-hidden="true"></i>
                    </button>
                </header>

                <form id="class-form" class="classes-form" novalidate @submit.prevent="submitClass">
                    <fieldset class="classes-fieldset">
                        <legend>Class details</legend>

                        <div v-if="!categories.length && !categoriesLoading" class="classes-notice">
                            <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
                            <span>No class categories are available. Contact your school administrator before adding classes.</span>
                        </div>

                        <div class="row">
                            <div class="col-sm-6">
                                <div class="form-group">
                                    <label for="class-category">
                                        Category <span class="classes-required">*</span>
                                    </label>
                                    <select
                                        id="class-category"
                                        v-model="form.categoryId"
                                        class="form-control"
                                        :class="{ 'is-invalid': errors.categoryId }"
                                        :disabled="isSaving || !categories.length"
                                        @change="clearError('categoryId')"
                                    >
                                        <option v-if="categoriesLoading" :value="0" disabled>
                                            Loading categories…
                                        </option>
                                        <option v-else :value="0">Select category</option>
                                        <option v-for="category in categories" :key="category.Id" :value="category.Id">
                                            {{ category.CategoryName }}
                                        </option>
                                    </select>
                                    <span v-if="errors.categoryId" class="classes-error" role="alert">
                                        {{ errors.categoryId }}
                                    </span>
                                    <span v-else class="classes-hint">
                                        Categories group classes of the same level.
                                    </span>
                                </div>
                            </div>

                            <div class="col-sm-6">
                                <div class="form-group">
                                    <label for="class-name">
                                        Class name <span class="classes-required">*</span>
                                    </label>
                                    <input
                                        id="class-name"
                                        v-model="form.className"
                                        type="text"
                                        class="form-control"
                                        :class="{ 'is-invalid': errors.className }"
                                        placeholder="e.g. JSS 1"
                                        maxlength="80"
                                        autocomplete="off"
                                        :disabled="isSaving"
                                        @input="clearError('className')"
                                    />
                                    <span v-if="errors.className" class="classes-error" role="alert">
                                        {{ errors.className }}
                                    </span>
                                    <span v-else class="classes-hint">
                                        Used on class lists, reports and result sheets.
                                    </span>
                                </div>
                            </div>
                        </div>
                    </fieldset>

                    <fieldset class="classes-fieldset">
                        <legend>Assessment weighting</legend>
                        <p class="classes-fieldset__note">
                            Maximum scores used when building results for this class. Leave a
                            field empty if it does not apply.
                        </p>

                        <div class="row">
                            <div class="col-6 col-sm-3">
                                <div class="form-group">
                                    <label for="class-exam">Exam</label>
                                    <input
                                        id="class-exam"
                                        v-model.number="form.examScore"
                                        type="number"
                                        min="0"
                                        step="1"
                                        inputmode="numeric"
                                        class="form-control"
                                        :class="{ 'is-invalid': errors.examScore }"
                                        :disabled="isSaving"
                                        @input="clearError('examScore')"
                                    />
                                    <span v-if="errors.examScore" class="classes-error" role="alert">
                                        {{ errors.examScore }}
                                    </span>
                                </div>
                            </div>

                            <div class="col-6 col-sm-3">
                                <div class="form-group">
                                    <label for="class-ca">CA</label>
                                    <input
                                        id="class-ca"
                                        v-model.number="form.caScore"
                                        type="number"
                                        min="0"
                                        step="1"
                                        inputmode="numeric"
                                        class="form-control"
                                        :class="{ 'is-invalid': errors.caScore }"
                                        :disabled="isSaving"
                                        @input="clearError('caScore')"
                                    />
                                    <span v-if="errors.caScore" class="classes-error" role="alert">
                                        {{ errors.caScore }}
                                    </span>
                                </div>
                            </div>

                            <div class="col-6 col-sm-3">
                                <div class="form-group">
                                    <label for="class-midterm">Mid term</label>
                                    <input
                                        id="class-midterm"
                                        v-model.number="form.midTermScore"
                                        type="number"
                                        min="0"
                                        step="1"
                                        inputmode="numeric"
                                        class="form-control"
                                        :class="{ 'is-invalid': errors.midTermScore }"
                                        :disabled="isSaving"
                                        @input="clearError('midTermScore')"
                                    />
                                    <span v-if="errors.midTermScore" class="classes-error" role="alert">
                                        {{ errors.midTermScore }}
                                    </span>
                                </div>
                            </div>

                            <div class="col-6 col-sm-3">
                                <div class="form-group">
                                    <label for="class-topic">Topic</label>
                                    <input
                                        id="class-topic"
                                        v-model.number="form.topicScore"
                                        type="number"
                                        min="0"
                                        step="1"
                                        inputmode="numeric"
                                        class="form-control"
                                        :class="{ 'is-invalid': errors.topicScore }"
                                        :disabled="isSaving"
                                        @input="clearError('topicScore')"
                                    />
                                    <span v-if="errors.topicScore" class="classes-error" role="alert">
                                        {{ errors.topicScore }}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div
                            class="classes-weight-summary"
                            :class="{ 'is-balanced': formHasScores && formTotal === 100 }"
                        >
                            <div class="classes-weight-summary__metric">
                                <span class="classes-weight-summary__label">Combined weight</span>
                                <strong class="classes-weight-summary__value">
                                    {{ formHasScores ? formTotal : "—" }}
                                </strong>
                            </div>
                            <p class="classes-weight-summary__note">
                                <template v-if="!formHasScores">
                                    Enter the maximum score for each assessment.
                                </template>
                                <template v-else-if="formTotal === 100">
                                    All assessments add up to 100.
                                </template>
                                <template v-else>
                                    Most schools weight these assessments to a total of 100.
                                </template>
                            </p>
                        </div>
                    </fieldset>
                </form>
            </div>

            <template slot="footer">
                <div class="classes-drawer__actions">
                    <button
                        type="button"
                        class="btn btn-outline-secondary"
                        :disabled="isSaving"
                        @click="closeForm"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        form="class-form"
                        class="btn classes-submit"
                        :disabled="isSaving || !categories.length"
                    >
                        <i
                            class="fa"
                            :class="isSaving ? 'fa-spinner fa-spin' : 'fa-check'"
                            aria-hidden="true"
                        ></i>
                        <span>{{ submitLabel }}</span>
                    </button>
                </div>
            </template>
        </modal>
    </div>
</template>

<script>
import PageSpinner from "@/components/PageSpinner";
import Alert from "@/mixins/Alert.mixins";
import ValidateAccessToken from "@/mixins/ValidateAccessToken.mixins";
import CheckStaffUser from "@/mixins/CheckStaffUser.mixin";
import modal from "@/components/Modal";

const SCORE_FIELDS = ["examScore", "caScore", "midTermScore", "topicScore"];

const EMPTY_FORM = () => ({
    categoryId: 0,
    className: "",
    examScore: "",
    caScore: "",
    midTermScore: "",
    topicScore: ""
});

export default {
    name: "school-class",
    mixins: [ValidateAccessToken, CheckStaffUser, Alert],
    components: {
        PageSpinner,
        modal
    },
    data() {
        return {
            accessDetails: null,
            classitems: null,
            classcategory: null,
            currentPage: 1,
            pageSize: 10,
            pageTotal: 0,
            totalRecords: null,
            searchval: "",
            isFetching: false,
            loadFailed: false,
            requestSeq: 0,
            searchTimer: null,
            formVisible: false,
            isSaving: false,
            editingId: 0,
            form: EMPTY_FORM(),
            errors: {},
            onloader: "none"
        };
    },
    computed: {
        classes() {
            return Array.isArray(this.classitems) ? this.classitems : [];
        },
        categories() {
            return Array.isArray(this.classcategory) ? this.classcategory : [];
        },
        categoriesLoading() {
            return this.classcategory === null;
        },
        initialLoading() {
            return this.classitems === null && !this.loadFailed;
        },
        totalPages() {
            return Math.max(0, Number(this.pageTotal) || 0);
        },
        hasSearch() {
            return this.searchval.trim().length > 0;
        },
        resultsLabel() {
            if (this.hasSearch) {
                return `Search results for “${this.searchval.trim()}”`;
            }
            return "Every class level and the weighting used for its assessments.";
        },
        paginationInfo() {
            if (this.totalPages <= 1) return "";
            const parts = [`Page ${this.currentPage} of ${this.totalPages}`];
            if (this.totalRecords !== null) {
                parts.push(`${this.totalRecords} ${this.totalRecords === 1 ? "class" : "classes"}`);
            }
            return parts.join(" · ");
        },
        paginationNumbers() {
            const total = this.totalPages;
            if (total <= 0) return [];
            if (total <= 7) {
                return Array.from({ length: total }, (_, index) => index + 1);
            }

            const current = this.currentPage;
            const wanted = [1, total, current, current - 1, current + 1, current - 2, current + 2]
                .filter(page => page >= 1 && page <= total)
                .sort((a, b) => a - b);

            const pages = [...new Set(wanted)];
            const numbers = [];
            let previous = 0;

            pages.forEach(page => {
                if (page - previous > 1) numbers.push("…");
                numbers.push(page);
                previous = page;
            });

            return numbers;
        },
        selectedCategoryName() {
            const category = this.categories.find(
                item => String(item.Id) === String(this.form.categoryId)
            );
            return category ? category.CategoryName : "";
        },
        editingLabel() {
            if (!this.editingId) return "";
            return [this.form.className, this.selectedCategoryName].filter(Boolean).join(" · ");
        },
        formHasScores() {
            return SCORE_FIELDS.some(field => this.toNumber(this.form[field]) !== null);
        },
        formTotal() {
            return SCORE_FIELDS.reduce(
                (total, field) => total + (this.toNumber(this.form[field]) || 0),
                0
            );
        },
        submitLabel() {
            if (this.isSaving) return "Saving…";
            return this.editingId ? "Save changes" : "Create class";
        }
    },
    watch: {
        pageSize() {
            if (this.classitems === null) return;
            this.currentPage = 1;
            this.loadClasses();
        },
        searchval() {
            this.scheduleSearch();
        }
    },
    methods: {
        toNumber(value) {
            if (value === null || value === undefined || value === "") return null;
            const parsed = Number(value);
            return Number.isFinite(parsed) ? parsed : null;
        },
        formatScore(value) {
            const parsed = this.toNumber(value);
            if (parsed !== null) return String(parsed);
            return value === null || value === undefined || value === "" ? "—" : String(value);
        },
        weightTotal(item) {
            if (!item) return null;
            const values = SCORE_FIELDS.map(field => this.toNumber(item[field]));
            if (values.every(value => value === null)) return null;
            return values.reduce((total, value) => total + (value === null ? 0 : value), 0);
        },
        formatWeight(item) {
            const total = this.weightTotal(item);
            return total === null ? "—" : String(total);
        },
        weightClass(item) {
            const total = this.weightTotal(item);
            if (total === null) return "is-empty";
            return total === 100 ? "is-balanced" : "is-off";
        },
        rowKey(item, index) {
            const id = item && (item.classId || item.Id || item.id);
            return id !== undefined && id !== null && id !== "" ? id : `class-${index}`;
        },
        editLabel(item) {
            return item && item.className ? `Edit ${item.className}` : "Edit class";
        },
        errorMessage(error, fallback) {
            const data = error && error.response && error.response.data;
            if (typeof data === "string" && data.trim()) return data.trim();
            if (data && typeof data === "object") {
                return data.message || data.responseMessage || fallback;
            }
            if (error && error.message) return error.message;
            return fallback;
        },
        async loadClasses() {
            const sequence = ++this.requestSeq;
            this.isFetching = true;

            try {
                const search = this.searchval.trim();
                const resp = await this.$store.dispatch("GetAllClass", {
                    PageNumber: this.currentPage,
                    PageSize: Number(this.pageSize) || 10,
                    SearchValue: search === "" ? "all" : search
                });

                if (sequence !== this.requestSeq) return;

                this.classitems = resp && Array.isArray(resp.data) ? resp.data : [];
                this.pageTotal = resp && resp.totalPages != null ? Number(resp.totalPages) || 0 : 0;
                this.totalRecords = resp && resp.totalRecords != null ? Number(resp.totalRecords) : null;
                this.loadFailed = false;
            } catch (error) {
                if (sequence !== this.requestSeq) return;
                this.loadFailed = true;
                this.ShowAlertError(
                    this.errorMessage(error, "Unable to load classes at the moment."),
                    ""
                );
            } finally {
                if (sequence === this.requestSeq) this.isFetching = false;
            }
        },
        async loadCategories() {
            try {
                const resp = await this.$store.dispatch("getclasscategory");
                this.classcategory = Array.isArray(resp) ? resp : [];
            } catch (error) {
                this.classcategory = [];
                this.ShowAlertError(
                    this.errorMessage(error, "Unable to load class categories."),
                    ""
                );
            }
        },
        retryLoad() {
            this.loadFailed = false;
            this.loadClasses();
        },
        scheduleSearch() {
            if (this.searchTimer) clearTimeout(this.searchTimer);
            this.searchTimer = setTimeout(() => {
                this.searchTimer = null;
                this.currentPage = 1;
                this.loadClasses();
            }, 400);
        },
        runSearchNow() {
            if (this.searchTimer) {
                clearTimeout(this.searchTimer);
                this.searchTimer = null;
            }
            this.currentPage = 1;
            this.loadClasses();
        },
        clearSearch() {
            this.searchval = "";
            this.runSearchNow();
        },
        goToPage(page) {
            if (typeof page !== "number") return;
            if (page < 1 || page > this.totalPages) return;
            if (page === this.currentPage) return;
            this.currentPage = page;
            this.loadClasses();
        },
        nextPage() {
            this.goToPage(this.currentPage + 1);
        },
        prevPage() {
            this.goToPage(this.currentPage - 1);
        },
        resetForm() {
            this.editingId = 0;
            this.errors = {};
            this.form = EMPTY_FORM();
        },
        openCreate() {
            this.resetForm();
            this.formVisible = true;
        },
        editClass(item) {
            if (!item) return;
            this.resetForm();
            this.editingId = item.classId || item.Id || 0;
            this.form = {
                categoryId:
                    item.categoryId !== null && item.categoryId !== undefined
                        ? item.categoryId
                        : 0,
                className: item.className || "",
                examScore: item.examScore === null || item.examScore === undefined ? "" : item.examScore,
                caScore: item.caScore === null || item.caScore === undefined ? "" : item.caScore,
                midTermScore:
                    item.midTermScore === null || item.midTermScore === undefined
                        ? ""
                        : item.midTermScore,
                topicScore:
                    item.topicScore === null || item.topicScore === undefined
                        ? ""
                        : item.topicScore
            };
            this.formVisible = true;
        },
        closeForm() {
            this.formVisible = false;
            this.resetForm();
        },
        handleFormClosed() {
            this.resetForm();
        },
        handleKeydown(event) {
            if (event.key !== "Escape") return;
            if (!this.formVisible || this.isSaving) return;
            this.closeForm();
        },
        clearError(field) {
            if (!this.errors[field]) return;
            const errors = { ...this.errors };
            delete errors[field];
            this.errors = errors;
        },
        validate() {
            const errors = {};
            const categoryId = this.form.categoryId;
            if (categoryId === 0 || categoryId === "0" || categoryId === "" || categoryId === null || categoryId === undefined) {
                errors.categoryId = "Choose the category this class belongs to.";
            }

            const className = String(this.form.className || "").trim();
            if (!className) {
                errors.className = "Enter the class name.";
            }

            SCORE_FIELDS.forEach(field => {
                const value = this.form[field];
                if (value === "" || value === null || value === undefined) return;
                const parsed = Number(value);
                if (!Number.isFinite(parsed)) {
                    errors[field] = "Enter a number.";
                } else if (parsed < 0) {
                    errors[field] = "Scores cannot be negative.";
                }
            });

            this.errors = errors;

            if (Object.keys(errors).length) {
                const fieldIds = {
                    categoryId: "class-category",
                    className: "class-name",
                    examScore: "class-exam",
                    caScore: "class-ca",
                    midTermScore: "class-midterm",
                    topicScore: "class-topic"
                };
                const firstField = Object.keys(errors)[0];
                const element = document.getElementById(fieldIds[firstField]);
                if (element) element.focus();
            }

            return Object.keys(errors).length === 0;
        },
        toScore(value) {
            const parsed = this.toNumber(value);
            return parsed === null ? 0 : parsed;
        },
        async submitClass() {
            if (this.isSaving) return;
            if (!this.validate()) return;

            const isEdit =
                this.editingId !== 0 &&
                this.editingId !== null &&
                this.editingId !== undefined &&
                this.editingId !== "";
            const payload = {
                SchoolClass: String(this.form.className || "").trim(),
                CategoryId: this.form.categoryId,
                ExamScore: this.toScore(this.form.examScore),
                CAScore: this.toScore(this.form.caScore),
                MidTermScore: this.toScore(this.form.midTermScore),
                TopicScore: this.toScore(this.form.topicScore)
            };
            if (isEdit) payload.ClassId = this.editingId;

            this.isSaving = true;
            this.onloader = "flex";
            try {
                const resp = await this.$store.dispatch(
                    isEdit ? "UpdateClass" : "CreateClass",
                    payload
                );

                if (!resp || resp.responseCode !== "00") {
                    this.ShowAlertError(
                        (resp && resp.responseMessage) || "The class could not be saved.",
                        ""
                    );
                    return;
                }

                if (!isEdit) this.currentPage = 1;
                this.closeForm();
                await this.loadClasses();
                this.ShowAlertSuccess(
                    isEdit ? "Class updated successfully." : "Class created successfully.",
                    ""
                );
            } catch (error) {
                this.ShowAlertError(
                    this.errorMessage(error, "Unable to save this class at the moment."),
                    ""
                );
            } finally {
                this.isSaving = false;
                this.onloader = "none";
            }
        }
    },
    mounted() {
        const userDetails = this.$store.getters["auth/_user"];
        if (!userDetails || userDetails.isSchoolStaff == false) {
            this.$store.dispatch("auth/Logout");
            this.$router.push("/login");
            return;
        }

        document.addEventListener("keydown", this.handleKeydown);
        this.loadClasses();
        this.loadCategories();
    },
    beforeDestroy() {
        document.removeEventListener("keydown", this.handleKeydown);
        if (this.searchTimer) clearTimeout(this.searchTimer);
    }
};
</script>

<style scoped>
.classes-page {
    min-height: 100%;
}

.classes-page__body {
    padding-bottom: 2.5rem;
}

.classes-panel {
    position: relative;
    overflow: hidden;
    border: 1px solid #e5eaf0;
    border-radius: 0.9rem;
    background: #fff;
    box-shadow: 0 0.6rem 1.75rem rgba(50, 70, 90, 0.1);
}

.classes-panel__intro {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 1.25rem;
    padding: 1.5rem 1.5rem 1.35rem;
    border-bottom: 1px solid #edf0f3;
}

.classes-panel__text {
    min-width: 0;
}

.classes-eyebrow {
    display: block;
    margin-bottom: 0.35rem;
    color: #8492a2;
    font-size: 0.66rem;
    font-weight: 700;
    letter-spacing: 0.11em;
}

.classes-panel__intro h1 {
    margin: 0;
    color: #293b52;
    font-size: 1.6rem;
    font-weight: 700;
    line-height: 1.2;
}

.classes-panel__intro p {
    max-width: 48rem;
    margin: 0.45rem 0 0;
    color: #7d8b9a;
    font-size: 0.88rem;
    line-height: 1.55;
}

.classes-panel__side {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 0.75rem;
}

.classes-stat {
    display: flex;
    align-items: baseline;
    gap: 0.4rem;
    padding: 0.5rem 0.85rem;
    border: 1px solid #e3eaf1;
    border-radius: 0.65rem;
    background: #f8fafc;
    color: #65758a;
    font-size: 0.76rem;
    font-weight: 600;
    white-space: nowrap;
}

.classes-stat strong {
    color: #2d6fae;
    font-size: 1rem;
    font-weight: 700;
}

.classes-add {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 0.5rem;
    margin: 0;
    padding: 0.62rem 1.05rem;
    border: 1px solid #1850b8;
    border-radius: 0.55rem;
    background: #1850b8;
    color: #fff;
    font-size: 0.82rem;
    font-weight: 600;
    line-height: 1.2;
    box-shadow: 0 0.35rem 0.9rem rgba(24, 80, 184, 0.2);
    transition: background-color 0.15s ease, box-shadow 0.15s ease;
}

.classes-add:hover,
.classes-add:focus {
    background: #144a9e;
    border-color: #144a9e;
    color: #fff;
    box-shadow: 0 0.45rem 1.1rem rgba(24, 80, 184, 0.26);
}

.classes-add:focus-visible {
    outline: 2px solid #1850b8;
    outline-offset: 2px;
}

.classes-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 1.15rem 1.5rem;
    border-bottom: 1px solid #edf0f3;
}

.classes-toolbar h2 {
    margin: 0;
    color: #344767;
    font-size: 1.05rem;
    font-weight: 700;
}

.classes-toolbar__meta {
    margin: 0.3rem 0 0;
    color: #8490a0;
    font-size: 0.76rem;
}

.classes-toolbar__tools {
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

.classes-page-size {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    margin: 0;
    color: #7b8998;
    font-size: 0.76rem;
    font-weight: 600;
    white-space: nowrap;
}

.classes-page-size select {
    height: 2.4rem;
    padding: 0 1.75rem 0 0.6rem;
    border: 1px solid #dbe3ea;
    border-radius: 0.5rem;
    background: #fff;
    color: #344767;
    font-size: 0.8rem;
    font-weight: 600;
    cursor: pointer;
}

.classes-page-size select:focus {
    border-color: #7ea8dd;
    outline: none;
    box-shadow: 0 0 0 0.2rem rgba(24, 80, 184, 0.12);
}

.classes-search {
    position: relative;
    display: flex;
    flex: 0 1 290px;
    align-items: center;
    min-width: 230px;
}

.classes-search > i {
    position: absolute;
    left: 0.8rem;
    color: #9aa6b3;
    font-size: 0.8rem;
    pointer-events: none;
}

.classes-search__input {
    width: 100%;
    height: 2.4rem;
    padding: 0.4rem 2.25rem 0.4rem 2.15rem;
    border: 1px solid #dbe3ea;
    border-radius: 0.55rem;
    background: #fff;
    color: #344767;
    font-size: 0.82rem;
    -webkit-appearance: none;
}

.classes-search__input::placeholder {
    color: #a2adba;
}

.classes-search__input::-webkit-search-cancel-button {
    display: none;
}

.classes-search__input:focus {
    border-color: #7ea8dd;
    outline: none;
    box-shadow: 0 0 0 0.2rem rgba(24, 80, 184, 0.12);
}

.classes-search__clear {
    position: absolute;
    right: 0.4rem;
    display: inline-flex;
    width: 1.75rem;
    height: 1.75rem;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: transparent;
    color: #9aa6b3;
    font-size: 0.72rem;
    cursor: pointer;
}

.classes-search__clear:hover,
.classes-search__clear:focus {
    background: #eef2f6;
    color: #526273;
    outline: none;
}

.classes-state {
    display: flex;
    min-height: 240px;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.45rem;
    padding: 2rem 1.5rem;
    color: #83909e;
    text-align: center;
}

.classes-state > i {
    margin-bottom: 0.35rem;
    color: #aab6c2;
    font-size: 1.6rem;
}

.classes-state strong {
    color: #526273;
    font-size: 0.95rem;
}

.classes-state span {
    max-width: 28rem;
    font-size: 0.8rem;
    line-height: 1.55;
}

.classes-state .classes-ghost-button {
    margin-top: 0.75rem;
}

.classes-table-wrap {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    transition: opacity 0.15s ease;
}

.classes-table-wrap.is-loading,
.classes-cards.is-loading {
    opacity: 0.5;
    pointer-events: none;
}

.classes-table {
    width: 100%;
    min-width: 900px;
    margin: 0;
    border-collapse: separate;
    border-spacing: 0;
}

.classes-table th {
    padding: 0.7rem 1.25rem;
    border-bottom: 1px solid #e8edf3;
    background: #f8fafc;
    color: #7b8998;
    font-size: 0.64rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-align: left;
    text-transform: uppercase;
    white-space: nowrap;
}

.classes-table td {
    padding: 0.85rem 1.25rem;
    border-bottom: 1px solid #edf0f3;
    color: #526273;
    font-size: 0.84rem;
    vertical-align: middle;
}

.classes-table tbody tr:last-child td {
    border-bottom: 0;
}

.classes-table tbody tr:hover {
    background: #fafbfd;
}

.classes-table .is-center {
    text-align: center;
}

.classes-table__action-head {
    width: 1%;
}

.classes-table__name {
    overflow-wrap: anywhere;
    color: #344767;
    font-weight: 700;
}

.classes-table__score {
    color: #44566c;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
}

.classes-table__action {
    width: 1%;
    text-align: right;
    white-space: nowrap;
}

.classes-category {
    display: inline-flex;
    align-items: center;
    padding: 0.18rem 0.5rem;
    border-radius: 999px;
    background: #eef4ff;
    color: #2d6fae;
    font-size: 0.68rem;
    font-weight: 700;
    white-space: nowrap;
}

.classes-weight {
    display: inline-flex;
    min-width: 2.2rem;
    align-items: center;
    justify-content: center;
    padding: 0.16rem 0.45rem;
    border-radius: 999px;
    background: #eef2f6;
    color: #526273;
    font-size: 0.68rem;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
}

.classes-weight.is-balanced {
    background: #eef4ff;
    color: #1850b8;
}

.classes-weight.is-off {
    background: #fdf3e3;
    color: #a36a16;
}

.classes-weight.is-empty {
    background: #f4f6f9;
    color: #9aa6b3;
}

.classes-edit {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.35rem 0.6rem;
    border: 1px solid #dbe3ea;
    border-radius: 0.45rem;
    background: #fff;
    color: #44566c;
    font-size: 0.75rem;
    font-weight: 600;
    cursor: pointer;
    transition: border-color 0.15s ease, color 0.15s ease, background-color 0.15s ease;
}

.classes-edit:hover,
.classes-edit:focus {
    border-color: #7ea8dd;
    background: #f5f9ff;
    color: #1850b8;
    outline: none;
}

.classes-cards {
    display: none;
    gap: 0.85rem;
    padding: 1.25rem 1.5rem;
}

.class-card {
    min-width: 0;
    padding: 1rem;
    border: 1px solid #e5eaf0;
    border-radius: 0.8rem;
    background: #fff;
    box-shadow: 0 0.25rem 0.75rem rgba(50, 70, 90, 0.05);
}

.class-card__head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.75rem;
}

.class-card__identity {
    display: grid;
    min-width: 0;
    gap: 0.2rem;
}

.class-card__name {
    overflow-wrap: anywhere;
    color: #344767;
    font-size: 0.95rem;
    font-weight: 700;
}

.class-card__category {
    color: #2d6fae;
    font-size: 0.74rem;
    font-weight: 600;
}

.class-card__scores {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 0.6rem;
    margin: 0.9rem 0 0;
    padding-top: 0.85rem;
    border-top: 1px dashed #e8edf3;
}

.class-card__scores dt {
    color: #99a4b1;
    font-size: 0.6rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
}

.class-card__scores dd {
    margin: 0.2rem 0 0;
    color: #44566c;
    font-size: 0.86rem;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
}

.class-card__actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 0.9rem;
    padding-top: 0.85rem;
    border-top: 1px solid #edf0f3;
}

.classes-ghost-button {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    margin: 0;
    padding: 0.4rem 0.7rem;
    border: 1px solid #dbe3ea;
    border-radius: 0.45rem;
    background: #fff;
    color: #44566c;
    font-size: 0.76rem;
    font-weight: 600;
    line-height: 1.3;
    transition: border-color 0.15s ease, color 0.15s ease, background-color 0.15s ease;
}

.classes-ghost-button:hover,
.classes-ghost-button:focus {
    border-color: #7ea8dd;
    background: #f5f9ff;
    color: #1850b8;
    outline: none;
}

.classes-pagination {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 1rem 1.5rem 1.25rem;
    border-top: 1px solid #edf0f3;
}

.classes-pagination__info {
    margin: 0;
    color: #7b8998;
    font-size: 0.76rem;
    font-weight: 600;
}

.classes-pagination__controls {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.35rem;
}

.classes-page-btn {
    display: inline-flex;
    min-width: 2.15rem;
    height: 2.15rem;
    align-items: center;
    justify-content: center;
    padding: 0 0.55rem;
    border: 1px solid #dbe3ea;
    border-radius: 0.45rem;
    background: #fff;
    color: #44566c;
    font-size: 0.76rem;
    font-weight: 600;
    cursor: pointer;
    transition: border-color 0.15s ease, color 0.15s ease, background-color 0.15s ease;
}

.classes-page-btn:hover:not(:disabled) {
    border-color: #7ea8dd;
    background: #f5f9ff;
    color: #1850b8;
}

.classes-page-btn:focus {
    outline: none;
    box-shadow: 0 0 0 0.2rem rgba(24, 80, 184, 0.14);
}

.classes-page-btn.is-active {
    border-color: #1850b8;
    background: #1850b8;
    color: #fff;
}

.classes-page-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.classes-page-gap {
    min-width: 1.4rem;
    color: #9aa6b3;
    font-size: 0.8rem;
    text-align: center;
}

/* ---------- Drawer form ---------- */

.classes-drawer {
    display: flex;
    min-height: 100%;
    flex-direction: column;
}

.classes-drawer__head {
    display: flex;
    align-items: flex-start;
    gap: 0.85rem;
    padding-bottom: 1.1rem;
    border-bottom: 1px solid #edf0f3;
}

.classes-drawer__icon {
    display: grid;
    flex: 0 0 2.6rem;
    width: 2.6rem;
    height: 2.6rem;
    place-items: center;
    border-radius: 0.7rem;
    background: #eef4ff;
    color: #1850b8;
    font-size: 1rem;
}

.classes-drawer__heading {
    min-width: 0;
}

.classes-drawer__eyebrow {
    display: block;
    margin-bottom: 0.15rem;
    color: #8492a2;
    font-size: 0.62rem;
    font-weight: 700;
    letter-spacing: 0.1em;
}

.classes-drawer__heading h2 {
    margin: 0;
    color: #344767;
    font-size: 1.15rem;
    font-weight: 700;
    line-height: 1.25;
}

.classes-drawer__heading p {
    margin: 0.3rem 0 0;
    color: #7d8b9a;
    font-size: 0.8rem;
    line-height: 1.45;
}

.classes-drawer__close {
    display: inline-flex;
    flex: 0 0 2rem;
    width: 2rem;
    height: 2rem;
    align-items: center;
    justify-content: center;
    margin-left: auto;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: transparent;
    color: #8492a2;
    font-size: 0.85rem;
    cursor: pointer;
    transition: background-color 0.15s ease, color 0.15s ease;
}

.classes-drawer__close:hover,
.classes-drawer__close:focus {
    background: #eef2f6;
    color: #344767;
    outline: none;
}

.classes-form {
    margin: 0;
}

.classes-form .row {
    margin-right: -7.5px;
    margin-left: -7.5px;
}

.classes-form .row > [class*="col-"] {
    padding-right: 7.5px;
    padding-left: 7.5px;
}

.classes-fieldset {
    margin: 1.35rem 0 0;
    padding: 0;
    border: 0;
}

.classes-fieldset legend {
    display: block;
    width: auto;
    margin: 0 0 0.85rem;
    padding: 0;
    color: #8492a2;
    font-size: 0.64rem;
    font-weight: 700;
    letter-spacing: 0.09em;
    text-transform: uppercase;
}

.classes-fieldset__note {
    margin: -0.35rem 0 0.9rem;
    color: #8a97a6;
    font-size: 0.75rem;
    line-height: 1.5;
}

.classes-notice {
    display: flex;
    align-items: flex-start;
    gap: 0.55rem;
    margin-bottom: 1rem;
    padding: 0.7rem 0.8rem;
    border: 1px solid #f0d9aa;
    border-radius: 0.6rem;
    background: #fff8e9;
    color: #77551d;
    font-size: 0.76rem;
    line-height: 1.5;
}

.classes-form .form-group {
    margin-bottom: 1rem;
}

.classes-form label {
    margin-bottom: 0.35rem;
    color: #44566c;
    font-size: 0.78rem;
    font-weight: 700;
}

.classes-required {
    color: #c23b4a;
    font-weight: 700;
}

.classes-form .form-control {
    height: 2.5rem;
    padding: 0.35rem 0.7rem;
    border: 1px solid #dbe3ea;
    border-radius: 0.5rem;
    background: #fff;
    color: #344767;
    font-size: 0.84rem;
}

.classes-form .form-control:focus {
    border-color: #7ea8dd;
    outline: none;
    box-shadow: 0 0 0 0.2rem rgba(24, 80, 184, 0.12);
}

.classes-form .form-control:disabled {
    background: #f4f6f9;
    color: #8492a2;
    cursor: not-allowed;
}

.classes-form .form-control.is-invalid {
    border-color: #d76a76;
    box-shadow: 0 0 0 0.2rem rgba(194, 59, 74, 0.1);
}

.classes-error {
    display: block;
    margin-top: 0.3rem;
    color: #c23b4a;
    font-size: 0.72rem;
    font-weight: 600;
}

.classes-hint {
    display: block;
    margin-top: 0.3rem;
    color: #8a97a6;
    font-size: 0.72rem;
    line-height: 1.45;
}

.classes-weight-summary {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem 1rem;
    margin-top: 0.35rem;
    padding: 0.85rem 1rem;
    border: 1px solid #e5eaf0;
    border-radius: 0.7rem;
    background: #f8fafc;
    transition: border-color 0.15s ease, background-color 0.15s ease;
}

.classes-weight-summary.is-balanced {
    border-color: #bcd4ef;
    background: #f5f9ff;
}

.classes-weight-summary__metric {
    display: flex;
    align-items: baseline;
    gap: 0.5rem;
}

.classes-weight-summary__label {
    color: #8492a2;
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.07em;
    text-transform: uppercase;
}

.classes-weight-summary__value {
    color: #344767;
    font-size: 1.1rem;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
}

.classes-weight-summary.is-balanced .classes-weight-summary__value {
    color: #1850b8;
}

.classes-weight-summary__note {
    margin: 0;
    color: #7d8b9a;
    font-size: 0.75rem;
    line-height: 1.45;
}

.classes-drawer__actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.6rem;
    width: 100%;
}

.classes-drawer__actions .btn {
    display: inline-flex;
    min-height: 2.5rem;
    align-items: center;
    justify-content: center;
    gap: 0.45rem;
    margin: 0;
    padding: 0.4rem 1.1rem;
    border-radius: 0.5rem;
    font-size: 0.82rem;
    font-weight: 600;
}

.classes-submit {
    border: 1px solid #1850b8;
    background: #1850b8;
    color: #fff;
}

.classes-submit:hover,
.classes-submit:focus {
    border-color: #144a9e;
    background: #144a9e;
    color: #fff;
}

.classes-submit:disabled {
    border-color: #b9c8dc;
    background: #b9c8dc;
    color: #fff;
    cursor: not-allowed;
}

@media (max-width: 1199.98px) {
    .classes-table-wrap {
        display: none;
    }

    .classes-cards {
        display: grid;
    }
}

@media (max-width: 767.98px) {
    .classes-panel__intro {
        align-items: flex-start;
    }

    .classes-toolbar {
        flex-direction: column;
        align-items: stretch;
    }

    .classes-toolbar__tools {
        flex-wrap: wrap;
    }

    .classes-search {
        flex: 1 1 100%;
        width: 100%;
    }
}

@media (max-width: 575.98px) {
    .classes-page__body {
        padding-bottom: 1.5rem;
    }

    .classes-panel__intro {
        flex-direction: column;
        gap: 1rem;
        padding: 1.15rem 1rem 1rem;
    }

    .classes-panel__intro h1 {
        font-size: 1.35rem;
    }

    .classes-panel__side {
        width: 100%;
        flex-wrap: wrap;
    }

    .classes-stat {
        flex: 1 1 auto;
    }

    .classes-add {
        flex: 1 1 auto;
        justify-content: center;
    }

    .classes-toolbar {
        padding: 1rem;
    }

    .classes-toolbar__tools {
        align-items: stretch;
        flex-direction: column;
        gap: 0.65rem;
    }

    .classes-page-size {
        justify-content: space-between;
    }

    .classes-page-size select {
        flex: 0 0 auto;
    }

    .classes-search {
        flex: 0 0 auto;
        min-width: 0;
    }

    .classes-cards {
        padding: 0.85rem;
    }

    .classes-pagination {
        flex-direction: column;
        align-items: stretch;
        padding: 0.9rem 1rem 1.1rem;
    }

    .classes-pagination__info {
        text-align: center;
    }

    .classes-pagination__controls {
        justify-content: center;
    }

    .classes-drawer__head {
        gap: 0.7rem;
    }

    .classes-drawer__actions {
        flex-direction: column-reverse;
    }

    .classes-drawer__actions .btn {
        width: 100%;
    }
}
</style>
